import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "../components/DataTable";
import RegisterNotice from "../components/RegisterNotice";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Common from "../commons/common";

import moment from "moment";

type DataIndex = keyof Types.NoticeDto;

function NoticeList() {
    const alert = useAlert();

    const [notices, setNotices] = useState<Array<Types.NoticeDto>>();
    const [currentNotice, setcurrentNotice] = useState<Types.NoticeDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteNotice, setOpenDeleteNotice] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.NoticeDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.NoticeDto> = [
        {
            align: "center",
            title: String.id,
            dataIndex: "noticeId",
            key: "noticeId",
            ...getColumnSearchProps("noticeId"),
        },
        {
            align: "left",
            title: String.title,
            dataIndex: "title",
            key: "title",
            ...getColumnSearchProps("title"),
        },
        {
            align: "center",
            title: "카테고리",
            dataIndex: "noticeType",
            key: "noticeType",
            render: (_, { noticeType }) => {
                return (
                    <span>
                        {noticeType === "NOTICE"
                            ? "공지사항"
                            : noticeType === "EVENT"
                            ? "이벤트"
                            : noticeType === "EVENT_ANNOUNCEMENT"
                            ? "이벤트 발표"
                            : String.dash}
                    </span>
                );
            },
        },
        {
            align: "center",
            title: "이벤트 상태",
            dataIndex: "noticeStatus",
            key: "noticeStatus",
            render: (_, { noticeStatus }) => {
                return <span>{noticeStatus === "EVENT_FINISHED" ? "종료" : noticeStatus === "EVENT_IN_PROGRESS" ? "진행" : String.dash}</span>;
            },
        },
        {
            align: "center",
            title: String.fixed,
            dataIndex: "fixed",
            key: "fixed",
            render: (_, { fixed }) => {
                return <span>{fixed === true ? "○" : "✕"}</span>;
            },
        },
        {
            align: "center",
            title: String.hits,
            dataIndex: "hits",
            key: "hits",
            render: (_, { hits }) => hits || String.dash,
        },
        {
            align: "center",
            title: String.registeredDate,
            dataIndex: "registeredDate",
            key: "registeredDate",
            render: (_, { registeredDate }) => {
                if (registeredDate === null) return String.dash;
                return moment(registeredDate).format(Common.FORMAT_DATE_TIME);
            },
        },
    ];

    const requestAxiosGetNotices = async () => {
        const response = await utilAxiosWithAuth().get(Request.NOTICE_LIST_URL);
        console.log(response.data);
        return response.data;
    };

    const { loading: loadingGetNotices, error: errorGetNotices, data: resultGetNotices, execute: requestGetNotices } = useAsyncAxios(requestAxiosGetNotices);

    useEffect(() => {
        if (resultGetNotices === null) return;
        console.log("resultGetNotices", resultGetNotices);

        setNotices(resultGetNotices.notices);
    }, [resultGetNotices]);

    useEffect(() => {
        if (errorGetNotices === null) return;

        //console.log("errorGetUsers", errorGetUsers);
        alert.setAlert(errorGetNotices.response.data.code, "Error", errorGetNotices.response.data.message);
    }, [errorGetNotices]);

    const requestAxiosRemoveNotice = async (notice: Types.NoticeDto) => {
        const response = await utilAxiosWithAuth().delete(Request.NOTICE_URL + "/" + notice?.noticeId);
        return response.data;
    };

    const {
        loading: loadingRemoveNotice,
        error: errorRemoveNotice,
        data: resultRemoveNotice,
        execute: requestRemoveNotices,
    } = useAsyncAxios(requestAxiosRemoveNotice);

    useEffect(() => {
        if (resultRemoveNotice === null) return;
        console.log("resultRemoveNotice", resultRemoveNotice);

        alert.setAlert(AlertType.SUCCESS, "공지사항 삭제 성공", "공지사항 삭제를 완료하였습니다.");

        requestGetNotices();
    }, [resultRemoveNotice]);

    useEffect(() => {
        if (errorRemoveNotice === null) return;

        alert.setAlert(errorRemoveNotice.response.data.code, "Error", errorRemoveNotice.response.data.message);
    }, [errorGetNotices]);

    useEffect(() => {
        requestGetNotices();
    }, []);

    const onRow = (value: Types.NoticeDto) => {
        setEditMode("none");
        setcurrentNotice(value);
    };

    const handleDeleteNotice = () => {
        if (currentNotice !== undefined) setOpenDeleteNotice(true);
    };

    const handleDeleteNoticeOk = () => {
        setOpenDeleteNotice(false);
        requestRemoveNotices(currentNotice);
    };

    const handleDeleteNoticeCancel = () => {
        setOpenDeleteNotice(false);
    };

    const onRegisterNotice = () => {
        requestGetNotices();
        setEditMode("none");
    };

    return (
        <>
            <Row justify={"space-between"}>
                <Col span={6}>
                    <Divider orientation="left">
                        <span>공지/이벤트</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            editMode === "add" ? setEditMode("none") : setEditMode("add");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                    <Button
                        onClick={() => {
                            editMode === "update" ? setEditMode("none") : setEditMode("update");
                        }}
                    >
                        {String.userUpdate}
                    </Button>
                    <Button onClick={() => handleDeleteNotice()} danger>
                        {String.userRemove}
                    </Button>
                </Space>
            </Row>
            <DataTable hasScroll={false} columns={columns} rowKey="noticeId" dataSource={notices} onRow={onRow} tableSize={30} />

            {editMode === "add" && <RegisterNotice editMode={editMode} onRegisterNotice={onRegisterNotice} />}

            {editMode !== "add" && currentNotice !== undefined && (
                <RegisterNotice editMode={editMode} notice={currentNotice} onRegisterNotice={onRegisterNotice} />
            )}

            {openDeleteNotice && (
                <Modal open={openDeleteNotice} onOk={handleDeleteNoticeOk} onCancel={handleDeleteNoticeCancel}>
                    {currentNotice?.title} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default NoticeList;
